'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import {
  enableOneSignalPushFromGesture,
  getOneSignalPushEnabled,
  getPushSupportState,
  isOneSignalSdkReady,
  subscribeToOneSignalReady,
} from '@/components/entities/analytics/components/OneSIgnal/utils/oneSignal.client';
import Toast from '@/components/ui/Toast/toast';
import { Env } from '@/consts/Env';
import { useUserMeQuery } from '@/queries/user/user.queries';
import { usePwaInstall } from './PwaInstallProvider';

const IOS_POST_INSTALL_PUSH_TOAST_STORAGE_KEY = 'pwa-ios-post-install-push-toast-shown';

export default function PwaPostInstallPushPrompt() {
  const t = useTranslations('pwa');
  const { data: user } = useUserMeQuery();
  const { isAppleDevice, isStandalone } = usePwaInstall();
  const appId = Env.NEXT_PUBLIC_ONE_SIGNAL_APP_ID;
  const [isSdkReady, setIsSdkReady] = useState(() => isOneSignalSdkReady());
  const hasScheduledToastRef = useRef(false);

  useEffect(() => {
    if (isOneSignalSdkReady()) {
      setIsSdkReady(true);
      return;
    }

    const unsubscribe = subscribeToOneSignalReady(() => {
      setIsSdkReady(true);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !appId || !isAppleDevice || !isStandalone || !isSdkReady || hasScheduledToastRef.current) {
      return;
    }

    const supportState = getPushSupportState();
    if (!supportState.supported) {
      return;
    }

    if (window.localStorage.getItem(IOS_POST_INSTALL_PUSH_TOAST_STORAGE_KEY) === 'true') {
      return;
    }

    let cancelled = false;
    hasScheduledToastRef.current = true;

    void (async () => {
      const pushEnabled = await getOneSignalPushEnabled(appId, user?.id);
      if (cancelled || pushEnabled) {
        return;
      }

      window.localStorage.setItem(IOS_POST_INSTALL_PUSH_TOAST_STORAGE_KEY, 'true');

      Toast({
        type: 'warning',
        title: t('post-install-push-title'),
        description: t('post-install-push-description'),
        acceptButtonText: t('post-install-push-action'),
        rejectButtonText: t('post-install-push-later'),
        onAccept: async () => {
          const result = await enableOneSignalPushFromGesture({
            id: user?.id,
            email: user?.email,
          });

          if (!result.enabled) {
            if (result.reason === 'permission_denied') {
              Toast({
                type: 'warning',
                title: t('push-permission-title'),
                description: t('push-permission-description'),
              });
              return;
            }

            Toast({
              type: 'error',
              title: t('push-error-title'),
              description: t('push-error-description'),
            });
          }
        },
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [appId, isAppleDevice, isSdkReady, isStandalone, t, user?.email, user?.id]);

  return null;
}
